import { useEffect, useState } from 'react'
import type { NewsletterIssue } from '../types/newsletter'
import { parseRss2Json, parseRssXml } from '../utils/newsletter'

const FEED_URL = 'https://rss.beehiiv.com/feeds/vmZIynmWTN.xml'
// Passes the complete feed through untouched, so every issue can be sorted.
const RAW_PROXY = `https://api.allorigins.win/raw?url=${encodeURIComponent(FEED_URL)}`
// Last resort: only returns a subset of issues.
const JSON_PROXY = `https://api.rss2json.com/v1/api.json?rss_url=${encodeURIComponent(FEED_URL)}`

type Status = 'loading' | 'success' | 'error'

export function useNewsletterIssues(limit = 3) {
  const [issues, setIssues] = useState<NewsletterIssue[]>([])
  const [status, setStatus] = useState<Status>('loading')

  useEffect(() => {
    const controller = new AbortController()
    const { signal } = controller

    const fetchXml = async (url: string) => {
      const res = await fetch(url, { signal })
      if (!res.ok) throw new Error('Feed request failed')
      return parseRssXml(await res.text(), limit)
    }

    // Try the full feed directly, then through a pass-through proxy if the
    // browser blocks the cross-origin request, then a JSON proxy.
    const load = async () => {
      try {
        return await fetchXml(FEED_URL)
      } catch (err) {
        if (signal.aborted) throw err
      }
      try {
        return await fetchXml(RAW_PROXY)
      } catch (err) {
        if (signal.aborted) throw err
        const res = await fetch(JSON_PROXY, { signal })
        if (!res.ok) throw new Error('Proxy request failed')
        const data = await res.json()
        if (data.status !== 'ok') throw new Error('Proxy returned an error')
        return parseRss2Json(data.items, limit)
      }
    }

    load()
      .then((result) => {
        if (result.length === 0) throw new Error('No issues')
        setIssues(result)
        setStatus('success')
      })
      .catch(() => {
        if (!signal.aborted) setStatus('error')
      })

    return () => controller.abort()
  }, [limit])

  return { issues, status }
}
